import { useEffect, useRef } from 'react'
import { useStore } from 'react-redux'
import { bindActionCreators } from 'redux'

import * as ActionsMapping from '@store/actions'
import { reducers } from '@store/reducers'

import { useAppLoader } from '@services/hooks/AppLoader'
import { useAppSnackbars } from '@services/hooks/AppSnackbars'

import { getEnviron, ucfirst } from '@services/utils'

import Config from '@features/config.json'
import MockData from '@features/store/mock.json'

const DEFAULT_DATA_CHECK_TIMER = 30000

export const useAppData = () => {
    const store = useStore()

    const actions = useRef({})
    const states = useRef({})
    const environ = useRef(getEnviron())
    const now = useRef(new Date())
    const timerReloadData = useRef()
    const duration = useRef(3000)

    const { show: showLoader } = useAppLoader()
    const { stack: showSnackbar } = useAppSnackbars()

    useEffect(() => {
        const showReducerLoader = Config.interface?.showReducerLoader ?? false
        if (showReducerLoader) showLoader('LOADING')

        loadActions()
        loadData()

        timerReloadData.current = setInterval(loadData, DEFAULT_DATA_CHECK_TIMER)
        return () => { clearInterval(timerReloadData.current) }
    }, [])

    const loadActions = () => {
        for (let reducer in reducers) {
            if (reducer === 'app') {
                actions.current[reducer] = bindActionCreators(
                    ActionsMapping[ucfirst(reducer)], store.dispatch
                )
            } else {
                actions.current[reducer] = bindActionCreators(
                    ActionsMapping.FeaturesStore[ucfirst(reducer)].action, store.dispatch
                )
            }
        }
    }

    const loadData = () => {
        now.current = new Date()
        states.current = store.getState()
        if (Config[environ.current]?.useMock) loadFromMock()
        else loadFromApi()
    }

    const loadFromMock = () => {
        for (let reducer in reducers) {
            if (MockData[reducer]) {
                let data = MockData[reducer]?.data ?? null
                if (data && actions.current[reducer].populate
                    && typeof actions.current[reducer].populate === 'function'
                    && reducer !== 'app') process(reducer, data)
            }
        }
    }

    const loadFromApi = () => {
        for (let reducer in reducers) {
            if (((actions.current[reducer].load && typeof actions.current[reducer].load === 'function')
                    || (actions.current[reducer].populate && typeof actions.current[reducer].populate === 'function'))
                && reducer !== 'app') {
                process(reducer)
            }
        }
    }

    const process = (reducer, mockData = null) => {
        let lastUpdate = (states.current[reducer].lastUpdate !== null)
            ? new Date(states.current[reducer].lastUpdate) : null

        let reducerConfig = Config[environ.current].reducers ?? {}
        let timerConfig = Config[environ.current].timer ?? {}
        let timer = reducerConfig[reducer]?.timer ?? timerConfig.dataCheck ?? DEFAULT_DATA_CHECK_TIMER
        let reload = reducerConfig[reducer]?.reload ?? false
        let notAtStart = reducerConfig[reducer]?.notAtStart ?? false

        if (reducerConfig[reducer]?.useMock) mockData = MockData[reducer]?.data ?? null

        if (!notAtStart && (!lastUpdate || isNaN(lastUpdate) || ((now.current.getTime() - lastUpdate.getTime() > timer) && reload))) {
            if (mockData) actions.current[reducer].populate(
                mockData,
                (reducerName) => {
                    loadDataSuccess(reducerName)
                }
            )
            else if (actions.current[reducer].load && typeof actions.current[reducer].load === 'function') {
                actions.current[reducer].load(
                    (reducerName) => { loadDataSuccess(reducerName) },
                    (result, reducerName) => { loadDataError(result, reducerName) }
                )
            }

            if (actions.current[reducer].setLastUpdate && typeof actions.current[reducer].setLastUpdate === 'function')
                actions.current[reducer].setLastUpdate(now.current.getTime())
        }
    }

    const loadDataSuccess = (reducerName) => {
        actions.current['app'].setLoaders(reducerName, { populated: 1 })
    }

    const loadDataError = (result, reducerName) => {
        showSnackbar(reducerName, `Error on loading: ${reducerName}`, 'warning', duration.current)
        duration.current =  duration.current + 250
        actions.current['app'].setLoaders(reducerName, { populated: 2 })
    }
}